import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Icon from "@/components/Icon";
import TextField from "@/components/TextField";
import { SOME_USERS_QUERY } from "@/pages/people/queries";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import { SEND_SLACK_REVIEW_REQUEST_MUTATION, SLACK_USER_QUERY } from "@/pages/jobs/queries";
import ReviewerTag, { Reviewer } from "./ReviewerTag";

export default function ApplicantReviewSlackRequestDialog({
  disabled,
  open,
  setOpen,
  setRequestVariables,
  requestVariables
}: {
  disabled: boolean;
  open: boolean;
  setOpen: (value: boolean) => void;
  setRequestVariables: (value: any) => void;
  requestVariables: any;
}) {
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [reviewersExpanded, setReviewersExpanded] = useState(false);

  const { data: userData, loading: usersLoading } = useQuery(SOME_USERS_QUERY, {
    variables: { search: userSearchTerm },
    skip: Object.keys(userSearchTerm).length === 0 || userSearchTerm === ""
  });

  const [fetchUserData] = useLazyQuery(SLACK_USER_QUERY);

  const [sendReviewRequestSlackMessage] = useMutation(SEND_SLACK_REVIEW_REQUEST_MUTATION);

  const { control, setValue } = useForm();
  const { jobPostId } = useParams();

  const origin = location.origin;
  const url = origin + "/jobs/job-post/" + jobPostId + "/applicants";

  const [requestLoading, setRequestLoading] = useState(false);

  const removeReviewer = (reviewer: Reviewer) => {
    setRequestVariables({
      ...requestVariables,
      reviewers: requestVariables.reviewers.filter((r: Reviewer) => r.id !== reviewer.id),
      reviewerIds: requestVariables.reviewerIds.filter((id: string) => id !== reviewer.id)
    });
  };

  const sendReviewRequest = () => {
    setRequestLoading(true);
    const sendRequest = async () => {
      try {
        const response = await sendReviewRequestSlackMessage({
          variables: {
            reviewerIds: requestVariables.reviewerIds,
            message: requestVariables.message,
            applicationsUrl: url,
            jobPostId: jobPostId
          }
        });

        if (response.data.postApplicationReviewRequest.success) {
          showSnackbar("Review request sent to Slack");
          setOpen(false);
        } else {
          showErrorSnackbar("Something went wrong, please try again later.");
        }
      } catch (error) {
        console.log(error);
        showErrorSnackbar("Something went wrong, please try again later.");
      } finally {
        setRequestLoading(false);
      }
    };
    sendRequest();
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setUserSearchTerm(debouncedSearchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearchTerm, setUserSearchTerm]);

  useEffect(() => {
    if (!open) {
      setValue("reviewers", "");
      setValue("message", "");
      setRequestVariables({ reviewers: [], reviewerIds: [], message: "" });
    }
  }, [open, setValue, setRequestVariables]);

  return (
    <Dialog
      open={open}
      onClosed={() => {
        setOpen(false);
      }}
      headline="Request Reviews in Slack"
      className="w-[600px] h-fit max-h-[90vh] overflow-y-auto"
      actions={
        <>
          <Button
            variant="text"
            onClick={() => {
              setOpen(false);
              setRequestVariables({ reviewers: [], reviewerIds: [], message: "" });
              setValue("reviewers", "");
              setValue("message", "");
            }}
            disabled={disabled || requestLoading}
          >
            Cancel
          </Button>

          <Button
            disabled={disabled || requestLoading}
            trailingIcon={false}
            icon={disabled || requestLoading ? "progress_activity" : "ios_share"}
            iconClassName={classNames((disabled || requestLoading) && "animate-spin w-6 h-6 !block")}
            onClick={() => sendReviewRequest()}
          >
            Send Message
          </Button>
        </>
      }
      hasFormElements
    >
      <div className="mt-2 space-y-6">
        <div>
          Requesting reviews sends a message to Slack with a link to this job, tagging the selected recipient(s). You
          may also include an optional note for additional context.
        </div>

        <div className="space-y-6">
          <Controller
            name="reviewers"
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <Autocomplete
                label="Request Reviewers"
                query={value}
                loadingResults={usersLoading}
                data={userData?.backofficeUsers || []}
                onChangeText={(string) => {
                  onChange(string);
                  setDebouncedSearchTerm(string);
                }}
                reset={() => {
                  onChange("");
                }}
                onSelect={async (value: Reviewer) => {
                  onChange(value.name);
                  if (value.email) {
                    const { data: userResponse } = await fetchUserData({ variables: { email: value.email } });
                    if (userResponse.slackUser) {
                      const recipientId = userResponse?.slackUser?.id;
                      const recipientUser = {
                        name: value.name,
                        email: value.email,
                        id: recipientId
                      };
                      setRequestVariables({
                        ...requestVariables,
                        reviewers: requestVariables.reviewers.some((selectedId: string) => selectedId === recipientId)
                          ? requestVariables.reviewers
                          : [...requestVariables.reviewers, recipientUser],
                        reviewerIds: requestVariables.reviewerIds.some(
                          (selectedId: string) => selectedId === recipientId
                        )
                          ? requestVariables.reviewerIds
                          : [...requestVariables.reviewerIds, recipientId]
                      });
                    } else {
                      showErrorSnackbar(`Could not find user ${value.name} in Slack.`);
                    }
                  } else {
                    showErrorSnackbar(`Could not find user ${value.name} in Slack.`);
                  }
                  onChange("");
                }}
                renderItem={(data: Reviewer) => <AvatarDetails title={`${data.name}`} text="" icon="person" />}
                error={fieldState.invalid}
                errorText={fieldState.error?.message}
              />
            )}
          />
          {requestVariables.reviewers && requestVariables.reviewers.length > 0 ? (
            <div className="border border-sys-brand-outline rounded-lg p-4">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setReviewersExpanded(!reviewersExpanded)}
              >
                <div className="flex items-center gap-2">
                  <Icon name={reviewersExpanded ? "expand_more" : "chevron_right"} size={24} />
                  <span className="text-label-large">Reviewers ({requestVariables.reviewers.length})</span>
                </div>
              </div>

              {reviewersExpanded && (
                <div className="mt-3 pt-3 border-t border-sys-brand-outline">
                  <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
                    {requestVariables.reviewers.map((reviewer: Reviewer) => (
                      <ReviewerTag key={reviewer.id} reviewer={reviewer} onRemove={removeReviewer} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <Controller
            name="message"
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <TextField
                onChangeText={(text) => {
                  onChange(text);
                  setRequestVariables({ ...requestVariables, message: text });
                }}
                value={value}
                label="Message"
                type="textarea"
                rows={6}
                className="w-full"
                error={fieldState.invalid}
                errorText={fieldState.error?.message}
              />
            )}
          />
        </div>
      </div>
    </Dialog>
  );
}
